import axiosInstance from "../api/axiosInstance";

/**
 * LessonV2 Service
 *
 * Read-only data source for V2 lessons managed by MD Automation.
 * Uses the same axiosInstance (and therefore the same Token + JWT auth)
 * as every other API call in dingo_user.
 */

/**
 * Fetch active LessonV2 entries for a given year.
 *
 * @param {number} yearId - The year PK.
 * @param {string} [status="active"] - Lesson status filter.
 * @returns {Promise<{isSuccessful: boolean, data: Array, error?: string}>}
 */
export const listLessonsV2 = async (yearId, status = "active") => {
  try {
    const response = await axiosInstance.post("api/lessons-v2/list", {
      yearId,
      status,
    });

    const items = response?.data?.data ?? response?.data ?? [];
    const normalizedItems = Array.isArray(items) ? items : [];

    return {
      isSuccessful: true,
      data: normalizedItems.sort(
        (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
      ),
    };
  } catch (error) {
    console.warn("[LessonV2] listLessonsV2 failed:", error?.message);
    return {
      isSuccessful: false,
      data: [],
      error:
        error?.response?.data?.message || error?.message || "Unknown error",
    };
  }
};

/**
 * Fetch the full detail for a single LessonV2, optionally including its questions.
 *
 * @param {number} id - The LessonV2 primary key.
 * @param {boolean} [includeQuestions=false] - Whether to include the lesson's questions.
 * @returns {Promise<{isSuccessful: boolean, data: object|null, error?: string}>}
 */
export const getLessonV2 = async (id, includeQuestions = false) => {
  try {
    const params = {};
    if (includeQuestions) {
      params.includeQuestions = true;
    }

    const response = await axiosInstance.get(`api/lessons-v2/${id}`, {
      params,
    });

    const detail = response?.data?.data ?? response?.data ?? null;

    return {
      isSuccessful: true,
      data: detail,
    };
  } catch (error) {
    console.warn("[LessonV2] getLessonV2 failed:", error?.message);
    return {
      isSuccessful: false,
      data: null,
      error:
        error?.response?.data?.message || error?.message || "Unknown error",
    };
  }
};
